import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import AddressCapture, { composeAddress } from "../components/AddressCapture";
import { safeCopyToClipboard } from "../lib/utils";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  Check, 
  Share2,
  Copy,
  QrCode,
  Mail,
  MessageSquare,
  ClipboardCheck,
  ArrowRight,
  AlertTriangle,
  Shield,
  Ticket,
  Heart,
  Gift
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { BacsMandateConfirm } from "../components/BacsMandateConfirm";
import PaymentReadinessBanner from "../components/PaymentReadinessBanner";
// Import shared age rules engine - SINGLE SOURCE OF TRUTH
import { 
  calculateAge, 
  evaluateAgeRules, 
  evaluateWithChoice, 
  getFieldsToReconcile,
  getPaymentFrequencies,
  getDisplayPrice,
  AGE_RULE_RESULTS 
} from "../utils/ageRules";

export default function ConversionForm() {
  const { interactionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const featureMode = useFeatureMode();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showConsolidation, setShowConsolidation] = useState(false);
  const [consolidationComplete, setConsolidationComplete] = useState(false);
  const [conversionId, setConversionId] = useState(null);
  const [referralLink, setReferralLink] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [interactionLocationId, setInteractionLocationId] = useState(null);
  const [consolidationItems, setConsolidationItems] = useState([]);
  const [selectedConsolidation, setSelectedConsolidation] = useState([]);
  const [savingConsolidation, setSavingConsolidation] = useState(false);
  const signatureRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);

  // BACS Direct Debit mandate confirmation (regular giving on Connect charities)
  const [showBacs, setShowBacs] = useState(false);
  const [bacsConfig, setBacsConfig] = useState(null);
  
  // Location state
  const [location, setLocation] = useState({ lat: null, long: null });
  
  // Get donation type from URL query param (pre-selected)
  const preSelectedType = searchParams.get('type') || 'regular_giving';
  
  // Postcode lookup state
  const [lookingUpPostcode, setLookingUpPostcode] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    postcode: "",
    addressLine1: "",
    addressLine2: "",
    townCity: "",
    county: "",
    phone: "",
    email: "",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    conversionType: preSelectedType,
    amount: "",
    lotteryEntries: "1",
    paymentFrequency: "monthly",  // "monthly" or "annual"
    paymentDatePreference: "",  // Required for Direct Debit + Regular Giving
    // Bank details (for regular giving only - NOT for one-off)
    sortCode: "",
    accountNumber: "",
    accountHolderName: "",  // Package 1A — required when bank details required
    consentTerms: false,
    consentMarketing: false,
    consentSms: false,
    consentEmail: false,
    consentCall: false,
    consentPost: false,  // New: Post consent
    giftAid: false,  // Gift Aid opt-in (only used if campaign.enableGiftAid)
    signature: "",
    // Package 1A — Direct Debit acceptance (client-supplied, backend validated)
    ddAccepted: false,
    ddAcceptedAt: null,
    ddWordingVersion: "v1",
  });

  // Package 1A — final-submission idempotency key. Generated once per form
  // session so retries reuse the same key; regenerated only on manual reset.
  const [idempotencyKey] = useState(() => {
    const rnd = () => (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(36).slice(2).padEnd(32, "0");
    return (rnd() + rnd()).slice(0, 64);
  });

  const [errors, setErrors] = useState({});
  
  // Age rule state
  const [calculatedAge, setCalculatedAge] = useState(null);
  const [ageRuleAction, setAgeRuleAction] = useState(null);  // null, "block", "callback", "self_complete", "callback_or_self_complete"
  const [selectedAgeOption, setSelectedAgeOption] = useState(null);  // For callback_or_self_complete choice
  const [showAgeWarning, setShowAgeWarning] = useState(false);

  // Request location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            long: position.coords.longitude
          });
        },
        (error) => {
        }
      );
    }
  }, []);

  // Restore form data from sessionStorage on mount
  useEffect(() => {
    const savedData = sessionStorage.getItem(`taptrack_form_${interactionId}`);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Don't restore signature, and respect URL type param
        setFormData(prev => ({ 
          ...prev, 
          ...parsed, 
          signature: "",
          conversionType: preSelectedType // URL param takes precedence
        }));
      } catch (e) {
      }
    }
  }, [interactionId, preSelectedType]);

  // Load campaign data for branding and CONFIG-DRIVEN FORM BEHAVIOUR
  useEffect(() => {
    const loadCampaign = async () => {
      try {
        // Get interaction to find campaign and locationId
        const intRes = await apiClient.get(`/interactions/${interactionId}`);
        if (intRes.data?.campaignId) {
          const campRes = await apiClient.get(`/campaigns/${intRes.data.campaignId}`);
          const campaignData = campRes.data;
          setCampaign(campaignData);
          
          // ==================== FORM CONFIG BINDING (CRITICAL) ====================
          // Initialize form state from campaign config - form must not operate independently
          setFormData(prev => ({ ...prev,
            minAge: campaignData.minAge,
            overAgeThreshold: campaignData.overAgeThreshold,
            overAgeAction: campaignData.overAgeAction,
            enableAnnualPayments: campaignData.enableAnnualPayments
          }));
          
          // If annual payments not enabled, ensure default is monthly
          if (!campaignData.enableAnnualPayments) {
            setFormData(prev => ({ ...prev, paymentFrequency: "monthly" }));
          }
        }
        // Store locationId from interaction
        if (intRes.data?.locationId) {
          setInteractionLocationId(intRes.data.locationId);
        }
      } catch (error) {
        toast.error("Failed to load form configuration");
      }
    };
    loadCampaign();
  }, [interactionId]);

  // Save form data to sessionStorage on change.
  // Package 1A §8 — sensitive fields NEVER persisted to sessionStorage.
  useEffect(() => {
    if (interactionId && !loading && !submitted) {
      const dataToSave = { ...formData };
      // Sensitive strip-list (Package 1A):
      delete dataToSave.signature;
      delete dataToSave.sortCode;
      delete dataToSave.accountNumber;
      delete dataToSave.accountHolderName;
      delete dataToSave.dobDay;
      delete dataToSave.dobMonth;
      delete dataToSave.dobYear;
      delete dataToSave.paymentDatePreference;
      delete dataToSave.ddAccepted;
      delete dataToSave.ddAcceptedAt;
      delete dataToSave.ddWordingVersion;
      sessionStorage.setItem(`taptrack_form_${interactionId}`, JSON.stringify(dataToSave));
    }
  }, [formData, interactionId, loading, submitted]);

  const clearFormStorage = () => {
    sessionStorage.removeItem(`taptrack_form_${interactionId}`);
  };

  // Package 1A — clear on unload / unmount to prevent any post-session leakage.
  useEffect(() => {
    const handler = () => {
      try { sessionStorage.removeItem(`taptrack_form_${interactionId}`); } catch (e) {}
    };
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [interactionId]);

  // ==================== AGE RULES ENGINE (USES SHARED MODULE) ====================
  // Previous age for reconciliation tracking
  const prevAgeRef = useRef(null);

  // Apply age rules when DOB changes - REAL-TIME ENFORCEMENT
  useEffect(() => {
    const age = calculateAge(formData.dobDay, formData.dobMonth, formData.dobYear);
    const prevAge = prevAgeRef.current;
    
    // Update calculated age
    setCalculatedAge(age);
    prevAgeRef.current = age;
    
    if (age === null || !campaign) {
      setAgeRuleAction(null);
      setShowAgeWarning(false);
      return;
    }
    
    // STATE RECONCILIATION: If DOB changed after entering data, reconcile state
    if (prevAge !== null && prevAge !== age) {
      const reconciliation = getFieldsToReconcile(prevAge, age, campaign, formData);
      
      if (reconciliation.clearBankDetails) {
        setFormData(prev => ({ ...prev, sortCode: "", accountNumber: "" }));
      }
      if (reconciliation.clearSignature) {
        setFormData(prev => ({ ...prev, signature: "" }));
      }
      if (reconciliation.resetAgeOption) {
        setSelectedAgeOption(null);
      }
    }
    
    // Evaluate rules using shared engine
    const ruleResult = selectedAgeOption 
      ? evaluateWithChoice(age, campaign, selectedAgeOption)
      : evaluateAgeRules(age, campaign);
    
    // Update state based on rule result
    if (ruleResult.result === AGE_RULE_RESULTS.VALID) {
      setAgeRuleAction(null);
      setShowAgeWarning(false);
    } else {
      setAgeRuleAction(ruleResult.result);
      setShowAgeWarning(ruleResult.showWarning);
      
      // Clear bank details if switching to callback flow
      if (ruleResult.isBankDisabled && (formData.sortCode || formData.accountNumber)) {
        setFormData(prev => ({ ...prev, sortCode: "", accountNumber: "" }));
      }
    }
  }, [formData.dobDay, formData.dobMonth, formData.dobYear, campaign, selectedAgeOption]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute derived rule state using shared engine (SINGLE SOURCE OF TRUTH)
  const currentRuleResult = useCallback(() => {
    const age = calculateAge(formData.dobDay, formData.dobMonth, formData.dobYear);
    if (age === null || !campaign) {
      return { result: AGE_RULE_RESULTS.VALID, isBankDisabled: false, isSubmitBlocked: false };
    }
    return selectedAgeOption 
      ? evaluateWithChoice(age, campaign, selectedAgeOption)
      : evaluateAgeRules(age, campaign);
  }, [formData.dobDay, formData.dobMonth, formData.dobYear, campaign, selectedAgeOption]);

  // STRICT ENFORCEMENT: These must match backend logic exactly
  const ruleState = currentRuleResult();
  const isBankDisabled = ruleState.isBankDisabled;
  const isSubmitBlocked = ruleState.isSubmitBlocked;

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  useEffect(() => {
    initCanvas();
  }, []);

  const initCanvas = () => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const getPos = (e) => {
    const canvas = signatureRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = signatureRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = signatureRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = signatureRef.current;
    setFormData({ ...formData, signature: canvas.toDataURL() });
  };

  const clearSignature = () => {
    const canvas = signatureRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setFormData({ ...formData, signature: "" });
  };

  // Sort code validation (XX-XX-XX format)
  const validateSortCode = (code) => {
    if (!code) return { valid: false, error: "Sort code is required" };
    const clean = code.replace(/[-\s]/g, "");
    if (clean.length !== 6) return { valid: false, error: "Sort code must be 6 digits" };
    if (!/^\d+$/.test(clean)) return { valid: false, error: "Sort code must contain only digits" };
    return { valid: true, error: "" };
  };

  // Account number validation (8 digits)
  const validateAccountNumber = (num) => {
    if (!num) return { valid: false, error: "Account number is required" };
    const clean = num.replace(/\s/g, "");
    if (clean.length !== 8) return { valid: false, error: "Account number must be 8 digits" };
    if (!/^\d+$/.test(clean)) return { valid: false, error: "Account number must contain only digits" };
    return { valid: true, error: "" };
  };

  // Format sort code as XX-XX-XX
  const formatSortCode = (value) => {
    const clean = value.replace(/[^0-9]/g, "").slice(0, 6);
    if (clean.length <= 2) return clean;
    if (clean.length <= 4) return `${clean.slice(0, 2)}-${clean.slice(2)}`;
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4)}`;
  };

  // Postcode lookup
  const handlePostcodeLookup = async () => {
    if (!formData.postcode.trim()) {
      toast.error("Please enter a postcode");
      return;
    }
    
    setLookingUpPostcode(true);
    setAddressResults([]);
    
    try {
      // Strip internal whitespace + encode so the postcode never produces an
      // un-encoded space in the URL path (ingress returns 404 for raw spaces).
      const cleanPostcode = formData.postcode.trim().replace(/\s+/g, "");
      const response = await apiClient.get(`/postcode/lookup/${encodeURIComponent(cleanPostcode)}`);
      if (response.data.success && response.data.addresses?.length > 0) {
        setAddressResults(response.data.addresses);
        setShowAddressDropdown(true);
      } else {
        toast.error(response.data.message || "No addresses found for this postcode");
      }
    } catch (error) {
      toast.error("Postcode lookup failed. Please enter address manually.");
    } finally {
      setLookingUpPostcode(false);
    }
  };

  // Select address from dropdown
  const selectAddress = (addr) => {
    const addressLine = [addr.line1, addr.line2, addr.city].filter(Boolean).join(", ");
    setFormData(prev => ({ ...prev, address: addressLine }));
    setShowAddressDropdown(false);
    setAddressResults([]);
  };

  const validate = () => {
    const newErrors = {};
    const isOneOff = formData.conversionType === "one_off";

    // Name always required (optional label for one-off but still validate if filled)
    if (!isOneOff) {
      if (!formData.firstName.trim()) newErrors.firstName = "Required";
      if (!formData.lastName.trim()) newErrors.lastName = "Required";
    }
    
    // Address, DOB, postcode only required for non-one-off
    if (!isOneOff) {
      if (campaign?.requireAddress !== false && !(formData.addressLine1 || formData.address).trim()) {
        newErrors.address = "Required";
      }
      if (campaign?.requireAddress !== false && !(formData.townCity || "").trim()) {
        newErrors.townCity = "Required";
      }
      if (!formData.postcode.trim()) newErrors.postcode = "Required";
      if (campaign?.requirePhone && !formData.phone.trim()) {
        newErrors.phone = "Required";
      }
      if (campaign?.requireEmail && !formData.email.trim()) {
        newErrors.email = "Required";
      }

      if (!formData.dobDay) newErrors.dobDay = "Required";
      if (!formData.dobMonth) newErrors.dobMonth = "Required";
      if (!formData.dobYear) newErrors.dobYear = "Required";
    
    // ==================== AGE RULE VALIDATION (USES SHARED ENGINE) ====================
    if (formData.dobDay && formData.dobMonth && formData.dobYear) {
      const age = calculateAge(formData.dobDay, formData.dobMonth, formData.dobYear);
      
      // Use shared rule engine for consistent validation
      const ruleResult = selectedAgeOption 
        ? evaluateWithChoice(age, campaign, selectedAgeOption)
        : evaluateAgeRules(age, campaign);
      
      // HARD BLOCK: Under age or blocked action
      if (ruleResult.result === AGE_RULE_RESULTS.UNDER_AGE) {
        newErrors.dob = `Must be at least ${campaign?.minAge || 18} years old`;
      } else if (ruleResult.isSubmitBlocked) {
        newErrors.dob = ruleResult.message || "Cannot submit with current age configuration";
      }
    }
    } // end if (!isOneOff)

    if (formData.conversionType !== "lottery" && !formData.amount) {
      newErrors.amount = "Required";
    }

    // Bank detail validation for regular giving AND lottery (not one-off - uses Stripe)
    // RULE: Bank validation only when bank fields are active (skip for callback flow)
    if ((formData.conversionType === "regular_giving" || formData.conversionType === "lottery") && !isBankDisabled) {
      if (!formData.paymentDatePreference) {
        newErrors.paymentDatePreference = "Please choose a payment date";
      }
      if (formData.sortCode) {
        const scResult = validateSortCode(formData.sortCode);
        if (!scResult.valid) newErrors.sortCode = scResult.error;
      }
      if (formData.accountNumber) {
        const anResult = validateAccountNumber(formData.accountNumber);
        if (!anResult.valid) newErrors.accountNumber = anResult.error;
      }
    }

    // Signature required for non-callback flows (callback flow doesn't need signature)
    // Signature also not required for one-off (uses Stripe)
    if (formData.conversionType !== "one_off" && !isBankDisabled && !formData.signature) {
      newErrors.signature = "Signature required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Show confirmation modal before actual submit
  const handleShowConfirmation = () => {
    if (!validate()) {
      toast.error("Please complete all required fields");
      return;
    }
    setShowConfirmation(true);
  };

  const handleSubmit = async () => {
    setShowConfirmation(false);
    setLoading(true);
    
    // One-off donations — Package 1A behaviour:
    // In statements-only mode, one-offs are stored securely (no Stripe).
    // We fall through to the shared /conversions POST below.
    // If financial execution were re-enabled, the legacy Stripe path would run.
    if (formData.conversionType === "one_off" && isFinancialExecutionAllowed(featureMode)) {
      try {
        const response = await apiClient.post("/payments/one-off", {
          interactionId,
          amount: parseFloat(formData.amount),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email || null,
          phone: formData.phone || null,
          consentMarketing: formData.consentMarketing,
          consentEmail: formData.consentEmail,
          consentSms: formData.consentSms,
          consentPhone: formData.consentCall,
          consentPost: formData.consentPost,
          giftAid: !!formData.giftAid,
          giftAidAcceptedAt: formData.giftAid ? new Date().toISOString() : null,
          originUrl: window.location.origin
        });
        
        // Redirect to Stripe checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          toast.error("Payment setup failed");
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to create payment");
        setLoading(false);
      }
      return;
    }
    
    // Lottery and Regular Giving use normal form submission
    try {
      const _needsBank = (formData.conversionType === "regular_giving" || formData.conversionType === "lottery") && !isBankDisabled;
      const response = await apiClient.post("/conversions", {
        interactionId,
        locationId: interactionLocationId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || composeAddress(formData),
        postcode: formData.postcode,
        addressLine1: formData.addressLine1 || null,
        addressLine2: formData.addressLine2 || null,
        townCity: formData.townCity || null,
        county: formData.county || null,
        dobDay: parseInt(formData.dobDay),
        dobMonth: parseInt(formData.dobMonth),
        dobYear: parseInt(formData.dobYear),
        conversionType: formData.conversionType,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        lotteryEntries: formData.conversionType === "lottery" ? parseInt(formData.lotteryEntries) : null,
        consentMarketing: formData.consentMarketing,
        consentTerms: formData.consentTerms,
        consentTermsVersion: formData.consentTerms ? "v1" : null,
        consentTermsAcceptedAt: formData.consentTerms ? new Date().toISOString() : null,
        consentWordingVersion: "v1",
        consentSms: formData.consentSms,
        consentEmail: formData.consentEmail,
        consentCall: formData.consentCall,
        consentPost: formData.consentPost,
        signature: isBankDisabled ? null : formData.signature,  // Signature optional for callback flow
        // Location capture
        lat: location.lat,
        long: location.long,
        // Bank details (for regular giving + lottery Direct Debit; skip if callback flow)
        sortCode: _needsBank && formData.sortCode ? formData.sortCode.replace(/[-\s]/g, "") : null,
        accountNumber: _needsBank && formData.accountNumber ? formData.accountNumber.replace(/\s/g, "") : null,
        // Package 1A — account-holder name (required when bank details required)
        accountHolderName: _needsBank ? (formData.accountHolderName || null) : null,
        // Payment frequency
        paymentFrequency: formData.paymentFrequency || "monthly",
        // Preferred Payment Date (Direct Debit + Regular Giving)
        paymentDatePreference: _needsBank ? (formData.paymentDatePreference || null) : null,
        // Direct Debit acceptance — required for regular_giving with bank
        ddAccepted: !!(_needsBank && formData.ddAccepted),
        ddAcceptedAt: _needsBank && formData.ddAccepted ? new Date().toISOString() : null,
        ddWordingVersion: _needsBank && formData.ddAccepted ? (formData.ddWordingVersion || "v1") : null,
        // Gift Aid (additive; only sent when campaign has enableGiftAid)
        giftAid: !!formData.giftAid,
        giftAidAcceptedAt: formData.giftAid ? new Date().toISOString() : null,
        // Age-based flow tracking
        requiresCallback: ageRuleAction === "callback" || (ageRuleAction === "callback_or_self_complete" && selectedAgeOption === "callback"),
        isSelfComplete: false,
        // Package 1A — final-submission idempotency key
        idempotencyKey,
      });

      setConversionId(response.data.id);

      // Regular giving routed to a Stripe Connect charity → confirm a BACS
      // Direct Debit mandate inline before finalising. Non-Connect charities
      // (or any non-applicable route) fall straight through to the normal flow.
      //
      // Package 0 Safety Freeze — do NOT make any /donations/bacs/setup request
      // while financial execution is disabled. The endpoint would return a
      // controlled 503 anyway, but making the call would leak a donor conversion
      // ID into the safety-audit log unnecessarily.
      if (
        formData.conversionType === "regular_giving" &&
        !isBankDisabled &&
        isFinancialExecutionAllowed(featureMode)
      ) {
        try {
          const bacsRes = await apiClient.post("/donations/bacs/setup", { conversionId: response.data.id });
          if (bacsRes.data?.applicable && bacsRes.data?.ready && bacsRes.data?.clientSecret) {
            setBacsConfig(bacsRes.data);
            setShowBacs(true);
            setLoading(false);
            return; // finalise after the donor confirms the mandate
          }
        } catch (e) {
          // Non-fatal — fall through to standard finalisation (manual BACS queue).
        }
      }

      await finalizeConversion(response.data.id, response.data.campaignId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit conversion");
    } finally {
      setLoading(false);
    }
  };

  const finalizeConversion = async (convId, campaignId) => {
    try {
      const referralRes = await apiClient.post(`/referrals/${convId}`);
      setReferralLink(referralRes.data);

      const campaignRes = await apiClient.get(`/campaigns/${campaignId}`);
      setCampaign(campaignRes.data);
      setConsolidationItems(campaignRes.data.consolidationChecklist || []);
    } catch (e) {
      // Referral / consolidation are enhancements — never block completion.
    }
    clearFormStorage();
    setSubmitted(true);
    toast.success("Conversion completed successfully!");
  };

  const copyReferralLink = async () => {
    if (referralLink) {
      const ok = await safeCopyToClipboard(referralLink.url);
      toast.success(ok ? "Link copied to clipboard!" : "Copy failed — select the link manually");
    }
  };

  const toggleConsolidationItem = (item) => {
    setSelectedConsolidation(prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  const handleSaveConsolidation = async () => {
    if (!conversionId) return;
    
    setSavingConsolidation(true);
    try {
      await apiClient.post(`/conversions/${conversionId}/consolidation`, {
        conversionId: conversionId,
        selectedItems: selectedConsolidation
      });
      setConsolidationComplete(true);
      toast.success(`Consolidation saved! Score: ${selectedConsolidation.length}/${consolidationItems.length}`);
    } catch (error) {
      toast.error("Failed to save consolidation");
    } finally {
      setSavingConsolidation(false);
    }
  };

  const proceedToConsolidation = () => {
    setShowConsolidation(true);
  };

  // Helper to get primary color or default
  const primaryColor = campaign?.primaryColor || "#10b981";
  const secondaryColor = campaign?.secondaryColor || "#8b5cf6";
  const buttonColor = campaign?.buttonColor || primaryColor;
  const logoUrl = campaign?.logoUrl;
  const headerImageUrl = campaign?.headerImageUrl;
  const tagline = campaign?.tagline;
  const brandName = campaign?.brandName || campaign?.name;
  const disclosureText = campaign?.disclosureText;
  const directDebitGuaranteeText = campaign?.directDebitGuaranteeText;
  const oneOffDisclosureText = campaign?.oneOffDisclosureText;
  const isOneOff = formData.conversionType === "one_off";
  const campaignName = campaign?.name || "Campaign";
  const hideTapTrackBranding = campaign?.hideTapTrackBranding || false;

  // Dynamic button style based on campaign color
  const primaryButtonStyle = {
    backgroundColor: buttonColor,
  };

  // Get type icon and label
  const getTypeDisplay = () => {
    switch (formData.conversionType) {
      case 'lottery':
        return { icon: Ticket, label: 'Lottery', color: 'text-violet-600', bg: 'bg-violet-100' };
      case 'regular_giving':
        return { icon: Heart, label: 'Regular Giving', color: 'text-emerald-600', bg: 'bg-emerald-100' };
      case 'one_off':
        return { icon: Gift, label: 'One-Off', color: 'text-amber-600', bg: 'bg-amber-100' };
      default:
        return { icon: Heart, label: 'Donation', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    }
  };

  const typeDisplay = getTypeDisplay();
  const TypeIcon = typeDisplay.icon;

  // Consolidation Complete Screen
  if (consolidationComplete) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="consolidation-complete">
        <header className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          {logoUrl ? (
            <img src={logoUrl} alt={campaignName} className="h-8 object-contain" />
          ) : (
            <div className="font-heading font-black text-xl text-slate-900">{campaignName}</div>
          )}
        </header>

        <main className="flex-1 p-6 max-w-md mx-auto w-full flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${primaryColor}20` }}>
            <Check className="h-10 w-10" style={{ color: primaryColor }} />
          </div>
          <h1 className="font-heading font-black text-2xl text-slate-900 mb-2 text-center">
            All Done!
          </h1>
          <p className="text-slate-500 text-center mb-4">
            Conversion recorded and consolidation complete.
          </p>
          <div className="rounded-xl p-4 mb-6 w-full" style={{ backgroundColor: `${primaryColor}10` }}>
            <div className="flex items-center justify-between">
              <span className="font-medium" style={{ color: primaryColor }}>Consolidation Score</span>
              <span className="font-heading font-black text-xl" style={{ color: primaryColor }}>
                {selectedConsolidation.length}/{consolidationItems.length}
              </span>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/dashboard")} 
            className="w-full h-14 text-white font-semibold rounded-xl" 
            style={primaryButtonStyle}
            data-testid="back-to-dashboard-btn"
          >
            Back to Dashboard
          </Button>
        </main>
      </div>
    );
  }

  // Consolidation Check Screen
  if (showConsolidation && submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="consolidation-check">
        <header className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          {logoUrl ? (
            <img src={logoUrl} alt={campaignName} className="h-8 object-contain" />
          ) : (
            <div className="font-heading font-black text-xl text-slate-900">{campaignName}</div>
          )}
        </header>

        <main className="flex-1 p-6 max-w-md mx-auto w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <ClipboardCheck className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-slate-900">
                Consolidation Check
              </h1>
              <p className="text-sm text-slate-500">
                Complete these steps to reinforce the conversion
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-8" data-testid="consolidation-checklist">
            {consolidationItems.map((item, index) => (
              <button
                key={`consolidation-${item}`}
                onClick={() => toggleConsolidationItem(item)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedConsolidation.includes(item)
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`consolidation-item-${index}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedConsolidation.includes(item)
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-slate-300'
                  }`}>
                    {selectedConsolidation.includes(item) && (
                      <Check className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <span className={`font-medium ${
                    selectedConsolidation.includes(item)
                      ? 'text-emerald-700'
                      : 'text-slate-700'
                  }`}>
                    {item}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-slate-600 font-medium">Progress</span>
              <span className="font-heading font-bold text-slate-900">
                {selectedConsolidation.length} / {consolidationItems.length}
              </span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${(selectedConsolidation.length / consolidationItems.length) * 100}%`,
                  backgroundColor: primaryColor
                }}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleSaveConsolidation}
              disabled={savingConsolidation}
              className="w-full h-14 text-white font-semibold rounded-xl"
              style={primaryButtonStyle}
              data-testid="save-consolidation-btn"
            >
              {savingConsolidation ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  Complete Consolidation
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConsolidationComplete(true)}
              className="w-full h-12 text-slate-500"
              data-testid="skip-consolidation-btn"
            >
              Skip for now
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Thank You / Share screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="conversion-success">
        <header className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          {logoUrl ? (
            <img src={logoUrl} alt={campaignName} className="h-8 object-contain" />
          ) : (
            <div className="font-heading font-black text-xl text-slate-900">{campaignName}</div>
          )}
        </header>

        <main className="flex-1 p-6 max-w-md mx-auto w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <Check className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
            <h1 className="font-heading font-black text-2xl text-slate-900 mb-2">
              Thank You, {formData.firstName}!
            </h1>
            <p className="text-slate-500">Your support means the world to us.</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-6 mb-6" data-testid="referral-section">
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="h-5 w-5" style={{ color: primaryColor }} />
              <h2 className="font-heading font-bold text-lg text-slate-900">Share with Friends</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Know someone who might want to help? Share your unique link!</p>

            <div className="bg-white p-4 rounded-lg mb-4 flex justify-center">
              {referralLink?.url ? (
                <QRCode value={referralLink.url} size={128} level="M" data-testid="qr-code" />
              ) : (
                <div className="w-32 h-32 bg-slate-100 rounded flex items-center justify-center">
                  <QrCode className="h-16 w-16 text-slate-300" />
                </div>
              )}
            </div>

            <div className="flex gap-2 mb-4">
              <Input value={referralLink?.url || ""} readOnly className="h-12 text-sm bg-white" data-testid="referral-link-input" />
              <Button variant="outline" className="h-12 px-4" onClick={copyReferralLink} data-testid="copy-link-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-12" data-testid="share-sms-btn"
                disabled={!referralLink?.url}
                onClick={() => {
                  if (!referralLink?.url) return;
                  const body = encodeURIComponent(`Support this cause — sign up here: ${referralLink.url}`);
                  window.location.href = `sms:?&body=${body}`;
                }}>
                <MessageSquare className="h-4 w-4 mr-2" />
                SMS
              </Button>
              <Button variant="outline" className="flex-1 h-12" data-testid="share-email-btn"
                disabled={!referralLink?.url}
                onClick={() => {
                  if (!referralLink?.url) return;
                  const subject = encodeURIComponent("Support this cause");
                  const body = encodeURIComponent(`I thought you might want to support this cause. You can sign up here:\n\n${referralLink.url}`);
                  window.location.href = `mailto:?subject=${subject}&body=${body}`;
                }}>
                <Mail className="h-4 w-4 mr-2" />
                Email
              </Button>
            </div>
          </div>

          <Button 
            onClick={proceedToConsolidation} 
            className="w-full h-14 text-white font-semibold rounded-xl" 
            style={primaryButtonStyle}
            data-testid="proceed-consolidation-btn"
          >
            <span className="flex items-center gap-2">
              Continue to Consolidation
              <ArrowRight className="h-5 w-5" />
            </span>
          </Button>
        </main>
      </div>
    );
  }

  // Main Form
  // Header background - with gradient overlay that fades to white
  const headerBgStyle = headerImageUrl 
    ? { 
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.4) 60%, rgba(255,255,255,0.9) 90%, rgba(255,255,255,1) 100%), url(${headerImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : { 
        background: `linear-gradient(to bottom, ${primaryColor} 0%, ${primaryColor}ee 60%, ${primaryColor}40 85%, transparent 100%)`
      };

  return (
    <div 
      className="min-h-screen relative"
      style={{ background: `linear-gradient(135deg, ${primaryColor}15 0%, #f8fafc 30%, #f8fafc 70%, ${primaryColor}10 100%)` }}
      data-testid="conversion-form"
    >
      {/* Header with campaign image or gradient - blends into form */}
      <div className="relative" style={headerBgStyle}>
        {/* Back Button - Fixed at top left */}
        <div className="absolute top-4 left-4 z-30">
          <button 
            onClick={() => navigate("/dashboard")} 
            className="text-white/90 hover:text-white font-medium text-sm flex items-center gap-1 px-3 py-2 rounded-lg bg-black/10 hover:bg-black/20 transition-colors"
            data-testid="back-btn"
          >
            ← Back
          </button>
        </div>

        {/* Spacer for header height */}
        <div className="pt-28 pb-6" />
      </div>

      {/* Form Card Container - With subtle brand glow */}
      <div className="max-w-xl mx-auto px-4 pb-8 -mt-8 relative z-10">
        <div 
          className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: `0 4px 24px -4px ${primaryColor}30, 0 8px 32px -8px rgba(0,0,0,0.12)` }}
        >
          {/* Donation Type Badge */}
          <div className="p-4 border-b border-slate-100">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${typeDisplay.bg}`}>
              <TypeIcon className={`h-5 w-5 ${typeDisplay.color}`} />
              <span className={`text-sm font-semibold ${typeDisplay.color}`}>{typeDisplay.label}</span>
            </div>
            {campaign?.id && <PaymentReadinessBanner campaignId={campaign.id} className="mt-3" />}
          </div>

          {/* Form Content */}
          <div className="p-6 md:p-8 space-y-8">
        
        {/* SECTION 1: Personal Details (full for regular, simplified for one-off) */}
        <section data-testid="section-personal">
          <h2 
            className="font-heading font-bold text-lg mb-4 pb-2 border-b-2"
            style={{ 
              color: primaryColor || '#1e293b',
              borderBottomColor: primaryColor || '#e2e8f0'
            }}
          >
            {isOneOff ? "Donation Amount" : "Personal Details"}
          </h2>

          {/* ONE-OFF: Amount input with preset buttons */}
          {isOneOff && (
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-4 gap-2">
                {[5, 10, 20, 50].map(preset => (
                  <Button
                    key={preset}
                    type="button"
                    variant={formData.amount === String(preset) ? "default" : "outline"}
                    className={`h-12 text-base font-bold rounded-xl ${formData.amount === String(preset) ? '' : ''}`}
                    style={formData.amount === String(preset) ? primaryButtonStyle : {}}
                    onClick={() => setFormData({ ...formData, amount: String(preset) })}
                    data-testid={`preset-amount-${preset}`}
                  >
                    £{preset}
                  </Button>
                ))}
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Amount (£) *</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="1"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="Enter amount"
                  className={`h-16 mt-1.5 text-2xl font-bold text-center ${errors.amount ? 'border-red-500' : 'border-slate-200'}`}
                  data-testid="one-off-amount-input"
                />
                {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
              </div>
              <p className="text-xs text-slate-500">Optional: Add your details below</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">First Name</Label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="h-14 mt-1.5 text-base border-slate-200"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Last Name</Label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="h-14 mt-1.5 text-base border-slate-200"
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-14 mt-1.5 text-base border-slate-200"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-14 mt-1.5 text-base border-slate-200"
                  data-testid="phone-input"
                />
              </div>
            </div>
          )}

          {/* REGULAR/LOTTERY: Full personal details */}
          {!isOneOff && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-medium">First Name *</Label>
                <Input
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className={`h-14 mt-1.5 text-base ${errors.firstName ? 'border-red-500' : 'border-slate-200'}`}
                  data-testid="first-name-input"
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Last Name *</Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={`h-14 mt-1.5 text-base ${errors.lastName ? 'border-red-500' : 'border-slate-200'}`}
                  data-testid="last-name-input"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <AddressCapture
              value={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              errors={errors}
              required={campaign?.requireAddress !== false}
              idPrefix="conv-addr"
            />

            <div>
              <Label className="text-slate-700 font-medium">Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-14 mt-1.5 text-base border-slate-200"
                data-testid="phone-input"
              />
            </div>

            <div>
              <Label className="text-slate-700 font-medium">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-14 mt-1.5 text-base border-slate-200"
                data-testid="email-input"
              />
            </div>
          </div>
          )}
        </section>

        {/* SECTION 2: Date of Birth - ONLY for non-one-off */}
        {!isOneOff && (
        <section data-testid="section-dob">
          <h2 
            className="font-heading font-bold text-lg mb-4 pb-2 border-b-2"
            style={{ 
              color: primaryColor || '#1e293b',
              borderBottomColor: primaryColor || '#e2e8f0'
            }}
          >
            Date of Birth
          </h2>
          <p className="text-slate-500 text-sm mb-4">Required to verify you are at least 18 years old.</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-slate-700 font-medium">Day *</Label>
              <Select value={formData.dobDay} onValueChange={(v) => setFormData({ ...formData, dobDay: v })}>
                <SelectTrigger className={`h-14 mt-1.5 ${errors.dobDay ? 'border-red-500' : ''}`} data-testid="dob-day-select">
                  <SelectValue placeholder="Day" />
                </SelectTrigger>
                <SelectContent>
                  {days.map((d) => (
                    <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Month *</Label>
              <Select value={formData.dobMonth} onValueChange={(v) => setFormData({ ...formData, dobMonth: v })}>
                <SelectTrigger className={`h-14 mt-1.5 ${errors.dobMonth ? 'border-red-500' : ''}`} data-testid="dob-month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Year *</Label>
              <Select value={formData.dobYear} onValueChange={(v) => setFormData({ ...formData, dobYear: v })}>
                <SelectTrigger className={`h-14 mt-1.5 ${errors.dobYear ? 'border-red-500' : ''}`} data-testid="dob-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {errors.dob && <p className="text-red-500 text-sm mt-2">{errors.dob}</p>}
          
          {/* Calculated Age Display */}
          {calculatedAge !== null && (
            <div className="mt-3 text-sm text-slate-600">
              Age: <span className="font-semibold">{calculatedAge}</span> years old
            </div>
          )}
          
          {/* Age Rule Warning */}
          {showAgeWarning && ageRuleAction && (
            <div className={`mt-4 p-4 rounded-xl border-2 ${
              ageRuleAction === "under_age" || ageRuleAction === "block" 
                ? "bg-red-50 border-red-300" 
                : "bg-amber-50 border-amber-300"
            }`} data-testid="age-warning-box">
              {ageRuleAction === "under_age" && (
                <div className="text-red-800">
                  <p className="font-semibold">Age Restriction</p>
                  <p className="text-sm mt-1">You must be at least {campaign?.minAge || 18} years old to sign up.</p>
                </div>
              )}
              
              {ageRuleAction === "block" && (
                <div className="text-red-800">
                  <p className="font-semibold">Online Signup Not Available</p>
                  <p className="text-sm mt-1">Due to your age, online signup is not available. Please contact us directly for assistance.</p>
                </div>
              )}
              
              {ageRuleAction === "callback" && (
                <div className="text-amber-800">
                  <p className="font-semibold">Callback Required</p>
                  <p className="text-sm mt-1">For your protection, a team member will contact you to complete your signup. Bank details are not required now.</p>
                </div>
              )}
              
              {ageRuleAction === "self_complete" && (
                <div className="text-amber-800">
                  <p className="font-semibold">Self-Completion Required</p>
                  <p className="text-sm mt-1 mb-4">For your protection, please complete this form on your own device using the referral link below.</p>
                  
                  {referralLink?.url ? (
                    <div className="bg-white rounded-xl p-4 border border-amber-200">
                      <div className="flex flex-col items-center gap-4">
                        <QRCode value={referralLink.url} size={120} level="M" />
                        <div className="w-full">
                          <p className="text-xs text-slate-500 mb-1">Or copy this link:</p>
                          <div className="flex gap-2">
                            <Input 
                              value={referralLink.url} 
                              readOnly 
                              className="h-10 text-xs bg-slate-50" 
                            />
                            <Button
                              type="button"
                              onClick={async () => {
                                const ok = await safeCopyToClipboard(referralLink.url);
                                toast.success(ok ? "Link copied!" : "Copy failed");
                              }}
                              className="h-10 px-3 bg-amber-500 hover:bg-amber-600 text-white"
                            >
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600">Ask the fundraiser for a referral link to continue at home.</p>
                  )}
                </div>
              )}
              
              {ageRuleAction === "callback_or_self_complete" && (
                <div className="text-amber-800">
                  <p className="font-semibold">Additional Verification Required</p>
                  <p className="text-sm mt-1 mb-3">For your protection, please choose how you'd like to proceed:</p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setSelectedAgeOption("callback")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        selectedAgeOption === "callback"
                          ? "border-amber-500 bg-amber-100"
                          : "border-amber-200 hover:bg-amber-100"
                      }`}
                      data-testid="age-option-callback"
                    >
                      <div className="font-medium">Request a Callback</div>
                      <div className="text-xs text-amber-600">A representative will contact you to complete signup</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedAgeOption("self_complete")}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        selectedAgeOption === "self_complete"
                          ? "border-amber-500 bg-amber-100"
                          : "border-amber-200 hover:bg-amber-100"
                      }`}
                      data-testid="age-option-self-complete"
                    >
                      <div className="font-medium">Complete on Your Own Device</div>
                      <div className="text-xs text-amber-600">Get a referral link to finish at home</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* SECTION 3: Donation Amount - ONLY for non-one-off (one-off has amount in section 1) */}
        {!isOneOff && (
        <section data-testid="section-donation">
          <h2 
            className="font-heading font-bold text-lg mb-4 pb-2 border-b-2"
            style={{ 
              color: primaryColor || '#1e293b',
              borderBottomColor: primaryColor || '#e2e8f0'
            }}
          >
            {formData.conversionType === 'lottery' ? 'Lottery Entry' : 
             formData.conversionType === 'regular_giving' ? 'Regular Donation' : 'Donation Amount'}
          </h2>
          
          {/* Payment Frequency Selector - CONFIG DRIVEN */}
          {/* Only shows when campaign.enableAnnualPayments is true */}
          {(() => {
            const frequencies = getPaymentFrequencies(campaign, formData.conversionType);
            const showSelector = frequencies.length > 1;
            
            if (!showSelector) return null;
            
            return (
              <div className="mb-4" data-testid="payment-frequency-section">
                <Label className="text-slate-700 font-medium">Payment Frequency</Label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  {frequencies.map(freq => (
                    <button
                      key={freq.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentFrequency: freq.value })}
                      className={`h-14 rounded-xl border-2 font-semibold transition-colors ${
                        formData.paymentFrequency === freq.value 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`payment-freq-${freq.value}`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          
          {formData.conversionType === "lottery" ? (
            <div>
              <Label className="text-slate-700 font-medium">Number of Entries</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, lotteryEntries: "1" })}
                  className={`h-24 rounded-xl border-2 font-semibold transition-colors ${
                    formData.lotteryEntries === "1" 
                      ? 'border-violet-500 bg-violet-50 text-violet-700' 
                      : 'border-slate-200 text-slate-700'
                  }`}
                  data-testid="lottery-single-btn"
                >
                  <div className="text-lg">Single</div>
                  <div className="text-2xl font-bold text-violet-600">
                    £{getDisplayPrice(campaign?.lotterySinglePrice || 5.00, formData.paymentFrequency)}
                    {formData.paymentFrequency === "annual" && <span className="text-sm font-normal">/year</span>}
                  </div>
                  <div className="text-xs font-normal text-slate-500">
                    {formData.paymentFrequency === "annual" ? "£5/month billed annually" : "1 entry per month"}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, lotteryEntries: "2" })}
                  className={`h-24 rounded-xl border-2 font-semibold transition-colors ${
                    formData.lotteryEntries === "2" 
                      ? 'border-violet-500 bg-violet-50 text-violet-700' 
                      : 'border-slate-200 text-slate-700'
                  }`}
                  data-testid="lottery-double-btn"
                >
                  <div className="text-lg">Double</div>
                  <div className="text-2xl font-bold text-violet-600">
                    £{getDisplayPrice(campaign?.lotteryDoublePrice || 10.00, formData.paymentFrequency)}
                    {formData.paymentFrequency === "annual" && <span className="text-sm font-normal">/year</span>}
                  </div>
                  <div className="text-xs font-normal text-slate-500">
                    {formData.paymentFrequency === "annual" ? "£10/month billed annually" : "2 entries per month"}
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-slate-700 font-medium">
                {formData.conversionType === "regular_giving" 
                  ? (formData.paymentFrequency === "annual" ? "Annual Amount (£) *" : "Monthly Amount (£) *")
                  : "Donation Amount (£) *"}
              </Label>
              {/* Preset Amount Buttons for Regular Giving */}
              {formData.conversionType === "regular_giving" && campaign?.regularPresets && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2 mb-3">
                  {(campaign.regularPresets || [5, 10, 15, 20, 25]).map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setFormData({ ...formData, amount: String(preset) })}
                      className={`h-12 rounded-lg border-2 font-semibold transition-colors ${
                        formData.amount === String(preset)
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      £{preset}
                    </button>
                  ))}
                </div>
              )}
              {formData.conversionType === "regular_giving" && (
                <p className="text-xs text-slate-500 mb-2">Or enter a custom amount:</p>
              )}
              <Input
                type="number"
                min="1"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className={`h-14 text-base ${errors.amount ? 'border-red-500' : 'border-slate-200'}`}
                placeholder="0.00"
                data-testid="amount-input"
              />
              {formData.conversionType === "regular_giving" && (
                <p className="text-xs text-slate-500 mt-1">This amount will be charged monthly</p>
              )}
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
          )}
          
          {/* Preferred Payment Date — Direct Debit + Regular Giving only */}
          {(formData.conversionType === "regular_giving" || formData.conversionType === "lottery") && !isBankDisabled && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <Label className="text-slate-700 font-medium" htmlFor="payment-date-select">Preferred Payment Date *</Label>
              <select
                id="payment-date-select"
                value={formData.paymentDatePreference}
                onChange={(e) => { setFormData({ ...formData, paymentDatePreference: e.target.value }); if (errors.paymentDatePreference) setErrors({ ...errors, paymentDatePreference: undefined }); }}
                className={`mt-1.5 w-full h-14 rounded-md border bg-white px-3 text-base ${errors.paymentDatePreference ? 'border-red-500' : 'border-slate-200'}`}
                data-testid="payment-date-select"
              >
                <option value="">Select payment date</option>
                <option value="1st">1st of each month</option>
                <option value="5th">5th of each month</option>
                <option value="10th">10th of each month</option>
                <option value="15th">15th of each month</option>
                <option value="20th">20th of each month</option>
                <option value="25th">25th of each month</option>
                <option value="last_working_day">Last working day of month</option>
              </select>
              <p className="text-xs text-slate-500 mt-1.5">Payments will be collected on or around your selected date.</p>
              {errors.paymentDatePreference && (
                <p className="text-red-500 text-xs mt-1" data-testid="payment-date-error">{errors.paymentDatePreference}</p>
              )}
            </div>
          )}

          {/* Bank Details Section - for regular giving + lottery Direct Debit (one-off uses Stripe) */}
          {(formData.conversionType === "regular_giving" || formData.conversionType === "lottery") && (
            <div className={`mt-6 pt-4 border-t border-slate-200 ${isBankDisabled ? 'opacity-50' : ''}`}>
              <h3 className="font-semibold text-slate-900 mb-3">
                Bank Details
                {isBankDisabled && <span className="ml-2 text-sm font-normal text-amber-600">(Not required for callback)</span>}
              </h3>
              {isBankDisabled ? (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    Bank details are not required for the callback flow. A representative will contact you to complete the setup securely.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-4" data-testid="bank-redacted">
                    Your submission is securely stored in Engage. Payment execution is disabled in Secure Export MVP mode — no payment is processed by OCI. Only the last 2 digits of the sort code and last 4 of the account number are retained for audit.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-slate-700 font-medium">Account Holder Name</Label>
                      <Input
                        value={formData.accountHolderName}
                        onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value.slice(0, 100) })}
                        placeholder="Name as it appears on the bank account"
                        disabled={isBankDisabled}
                        className={`h-14 mt-1.5 text-base ${errors.accountHolderName ? 'border-red-500' : 'border-slate-200'}`}
                        data-testid="account-holder-name-input"
                      />
                      {errors.accountHolderName && <p className="text-red-500 text-xs mt-1">{errors.accountHolderName}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Sort Code</Label>
                      <Input
                        value={formData.sortCode}
                        onChange={(e) => setFormData({ ...formData, sortCode: formatSortCode(e.target.value) })}
                        placeholder="00-00-00"
                        maxLength={8}
                        disabled={isBankDisabled}
                        className={`h-14 mt-1.5 text-base font-mono ${errors.sortCode ? 'border-red-500' : 'border-slate-200'}`}
                        data-testid="sort-code-input"
                      />
                      {errors.sortCode && <p className="text-red-500 text-xs mt-1">{errors.sortCode}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Account Number</Label>
                      <Input
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                        placeholder="12345678"
                        maxLength={8}
                        disabled={isBankDisabled}
                        className={`h-14 mt-1.5 text-base font-mono ${errors.accountNumber ? 'border-red-500' : 'border-slate-200'}`}
                        data-testid="account-number-input"
                      />
                      {errors.accountNumber && <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* One-Off Payment Notice — Package 1A: truthful in statements-only mode */}
          {isOneOff && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200" data-testid="one-off-truthful-notice">
                <h3 className="font-semibold text-blue-900 mb-2">Secure Donor Submission</h3>
                <p className="text-sm text-blue-700">
                  Your donation details have been securely submitted to the charity. No payment has been taken by OCI.
                </p>
              </div>
            </div>
          )}
        </section>
        )}

        {/* SECTION 4: Consent */}
        <section data-testid="section-consent">
          <h2 
            className="font-heading font-bold text-lg mb-4 pb-2 border-b-2"
            style={{ 
              color: primaryColor || '#1e293b',
              borderBottomColor: primaryColor || '#e2e8f0'
            }}
          >
            Consent
          </h2>
          
          {/* Disclosure texts for regular giving (Direct Debit compliance) */}
          {!isOneOff && (disclosureText || directDebitGuaranteeText) && (
            <div className="space-y-3 mb-4">
              {disclosureText && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 leading-relaxed" data-testid="disclosure-box">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>{disclosureText}</div>
                  </div>
                </div>
              )}
              {directDebitGuaranteeText && (
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800 leading-relaxed" data-testid="dd-guarantee-box">
                  <div className="flex items-start gap-2">
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>{directDebitGuaranteeText}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* One-off disclosure text */}
          {isOneOff && oneOffDisclosureText && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 leading-relaxed mb-4" data-testid="oneoff-disclosure-box">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>{oneOffDisclosureText}</div>
              </div>
            </div>
          )}
          
          {/* Default GDPR notice - only shown if no custom disclosure */}
          {!disclosureText && !isOneOff && (
            <div className="p-4 rounded-xl bg-slate-50 text-sm text-slate-600 leading-relaxed mb-4">
              <p>By proceeding, you agree to our Terms of Service and Privacy Policy. Your data will be processed in accordance with GDPR regulations.</p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* SMS consent toggle - respects campaign.allowSmsConsent */}
            {(campaign?.allowSmsConsent !== false) && (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="consentSms" className="text-slate-700 font-normal leading-relaxed cursor-pointer">
                  I consent to receiving SMS messages
                </Label>
                <Switch
                  id="consentSms"
                  checked={formData.consentSms}
                  onCheckedChange={(v) => setFormData({ ...formData, consentSms: v })}
                  data-testid="consent-sms-toggle"
                />
              </div>
            )}

            {/* Email consent toggle - respects campaign.allowEmailConsent */}
            {(campaign?.allowEmailConsent !== false) && (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="consentEmail" className="text-slate-700 font-normal leading-relaxed cursor-pointer">
                  I consent to receiving email communications
                </Label>
                <Switch
                  id="consentEmail"
                  checked={formData.consentEmail}
                  onCheckedChange={(v) => setFormData({ ...formData, consentEmail: v })}
                  data-testid="consent-email-toggle"
                />
              </div>
            )}

            {/* Phone consent toggle - respects campaign.allowPhoneConsent */}
            {(campaign?.allowPhoneConsent !== false) && (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="consentCall" className="text-slate-700 font-normal leading-relaxed cursor-pointer">
                  I consent to receiving phone calls
                </Label>
                <Switch
                  id="consentCall"
                  checked={formData.consentCall}
                  onCheckedChange={(v) => setFormData({ ...formData, consentCall: v })}
                  data-testid="consent-call-toggle"
                />
              </div>
            )}

            {/* Post consent toggle - respects campaign.allowPostConsent */}
            {(campaign?.allowPostConsent !== false) && (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="consentPost" className="text-slate-700 font-normal leading-relaxed cursor-pointer">
                  I consent to receiving post
                </Label>
                <Switch
                  id="consentPost"
                  checked={formData.consentPost}
                  onCheckedChange={(v) => setFormData({ ...formData, consentPost: v })}
                  data-testid="consent-post-toggle"
                />
              </div>
            )}
          </div>
        </section>

        {/* SECTION 4b: Gift Aid (additive; shown only when campaign.enableGiftAid) */}
        {campaign?.enableGiftAid && (
          <section data-testid="section-giftaid" className="rounded-xl p-5 -mx-2" style={{ backgroundColor: `${primaryColor}05`, border: `1px solid ${primaryColor}15` }}>
            <h2
              className="font-heading font-bold text-lg mb-3 pb-2 border-b-2 flex items-center gap-2"
              style={{ color: primaryColor || '#1e293b', borderBottomColor: primaryColor || '#e2e8f0' }}
            >
              <Gift className="h-5 w-5" /> Gift Aid
            </h2>
            <div className="flex items-start gap-3">
              <Checkbox
                id="giftAid"
                checked={!!formData.giftAid}
                onCheckedChange={(v) => setFormData({ ...formData, giftAid: !!v })}
                data-testid="gift-aid-checkbox"
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="giftAid" className="text-slate-900 font-medium cursor-pointer" data-testid="gift-aid-label">
                  I want to Gift Aid this donation
                </Label>
                {formData.giftAid && (campaign?.giftAidStatement || '').trim() !== '' && (
                  <div className="mt-3 p-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="gift-aid-statement">
                    {campaign.giftAidStatement}
                  </div>
                )}
                {formData.giftAid && !(campaign?.giftAidStatement || '').trim() && (
                  <div className="mt-3 p-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 leading-relaxed" data-testid="gift-aid-statement">
                    I am a UK taxpayer and want this charity to reclaim tax on this donation and any donations I make in the future or have made in the past four years. I understand I must pay enough UK tax to cover the amount reclaimed.
                  </div>
                )}
                {formData.giftAid && Number(formData.amount) > 0 && (
                  <div className="mt-2 text-xs font-medium text-emerald-700" data-testid="gift-aid-estimated-value">
                    + £{(Number(formData.amount) * 0.25).toFixed(2)} reclaimed via Gift Aid (25%)
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 5: Signature - ONLY for non-one-off (one-off uses Stripe) */}
        {!isOneOff && (
        <section 
          data-testid="section-signature"
          className="rounded-xl p-5 -mx-2"
          style={{ backgroundColor: `${primaryColor}08`, border: `1px solid ${primaryColor}15` }}
        >
          <h2 
            className="font-heading font-bold text-lg mb-4 pb-2 border-b-2"
            style={{ 
              color: primaryColor || '#1e293b',
              borderBottomColor: primaryColor || '#e2e8f0'
            }}
          >
            Signature
          </h2>
          
          <p className="text-slate-500 text-sm mb-4">Please sign below to confirm your donation.</p>
          <div 
            className={`rounded-xl overflow-hidden ${errors.signature ? 'border-red-500' : ''}`}
            style={{ border: `2px solid ${errors.signature ? '#ef4444' : primaryColor}40` }}
          >
            <canvas
              ref={signatureRef}
              width={350}
              height={150}
              className="w-full touch-none"
              style={{ backgroundColor: `${primaryColor}05` }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
              data-testid="signature-canvas"
            />
          </div>
          <Button type="button" variant="outline" onClick={clearSignature} className="w-full h-10 mt-3 rounded-xl" data-testid="clear-signature-btn">
            Clear Signature
          </Button>
          {errors.signature && <p className="text-red-500 text-sm mt-2">{errors.signature}</p>}
        </section>
        )}

        {/* Package 1A — Explicit terms + Direct Debit acceptance (required for regular_giving with bank) */}
        {!isOneOff && (
        <section data-testid="section-consent-terms" className="rounded-xl p-5 -mx-2 space-y-3 bg-slate-50 border border-slate-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5"
              checked={!!formData.consentTerms}
              onChange={(e) => setFormData({ ...formData, consentTerms: e.target.checked })}
              data-testid="consent-terms-checkbox"
            />
            <span className="text-sm text-slate-700">
              I accept the terms and conditions and consent to my details being securely stored by the charity.
            </span>
          </label>
          {(formData.conversionType === "regular_giving") && !isBankDisabled && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5"
                checked={!!formData.ddAccepted}
                onChange={(e) => setFormData({ ...formData, ddAccepted: e.target.checked })}
                data-testid="dd-accepted-checkbox"
              />
              <span className="text-sm text-slate-700">
                I confirm I am the account holder and the only person required to authorise debits on this account. I accept the Direct Debit Guarantee wording (v1).
              </span>
            </label>
          )}
        </section>
        )}

        {/* Submit Button - Primary CTA */}
        <div className="pt-6 border-t border-slate-200">
          {isSubmitBlocked && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-center text-sm text-red-800">
              {ageRuleAction === "under_age" && "Form submission blocked: Age requirement not met"}
              {ageRuleAction === "block" && "Online signup not available for this age range"}
              {ageRuleAction === "self_complete" && "Please request a referral link to complete on your own device"}
              {ageRuleAction === "callback_or_self_complete" && !selectedAgeOption && "Please select a verification option above"}
            </div>
          )}
          <Button
            onClick={handleShowConfirmation}
            disabled={loading || isSubmitBlocked}
            className={`w-full h-14 text-white font-bold text-base rounded-xl active:scale-[0.98] transition-all ${
              isSubmitBlocked ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            style={{ 
              ...primaryButtonStyle,
              boxShadow: isSubmitBlocked ? 'none' : `0 4px 14px -2px ${primaryColor}50, 0 2px 6px -1px ${primaryColor}30`
            }}
            data-testid="submit-btn"
          >
            <span className="flex items-center justify-center gap-2">
              <Check className="h-5 w-5" />
              {isOneOff ? "Pay Now" : isBankDisabled ? "Submit for Callback" : "Review & Submit"}
            </span>
          </Button>
        </div>

          </div>{/* End of form content padding div */}
        </div>{/* End of form card */}
      </div>{/* End of max-w-xl container */}

      {/* Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Your Details</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4" data-testid="confirmation-summary">
            {/* Personal (conditional for one-off) */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-2">{isOneOff ? "Donor Details" : "Personal Details"}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {(formData.firstName || formData.lastName) && (
                  <>
                    <div className="text-slate-500">Name</div>
                    <div className="text-slate-900">{formData.firstName} {formData.lastName}</div>
                  </>
                )}
                {!isOneOff && (
                  <>
                    <div className="text-slate-500">Address</div>
                    <div className="text-slate-900">{formData.address}, {formData.postcode}</div>
                    <div className="text-slate-500">DOB</div>
                    <div className="text-slate-900">{formData.dobDay}/{formData.dobMonth}/{formData.dobYear}</div>
                  </>
                )}
                {formData.email && (
                  <>
                    <div className="text-slate-500">Email</div>
                    <div className="text-slate-900">{formData.email}</div>
                  </>
                )}
              </div>
            </div>
            
            {/* Donation */}
            <div className="rounded-xl p-4" style={{ backgroundColor: `${primaryColor}10` }}>
              <h3 className="font-semibold mb-2" style={{ color: primaryColor }}>Donation</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div style={{ color: primaryColor }}>Type</div>
                <div className="text-slate-900 capitalize">{typeDisplay.label}</div>
                {formData.conversionType === "lottery" ? (
                  <>
                    <div style={{ color: primaryColor }}>Entries</div>
                    <div className="text-slate-900">{formData.lotteryEntries === "2" ? "Double" : "Single"}</div>
                    <div style={{ color: primaryColor }}>Amount</div>
                    <div className="text-slate-900 font-bold">
                      £{formData.lotteryEntries === "2" 
                        ? (campaign?.lotteryDoublePrice || 10).toFixed(2) 
                        : (campaign?.lotterySinglePrice || 5).toFixed(2)}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ color: primaryColor }}>Amount</div>
                    <div className="text-slate-900 font-bold">
                      £{formData.amount || '0.00'}
                      {formData.conversionType === "regular_giving" && '/month'}
                    </div>
                    {formData.sortCode && (
                      <>
                        <div style={{ color: primaryColor }}>Bank</div>
                        <div className="text-slate-900 font-mono text-xs">
                          ••-••-{formData.sortCode.slice(-2)} / ••••{formData.accountNumber?.slice(-4) || ''}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Signature preview */}
            {formData.signature && (
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <h3 className="font-semibold text-slate-900 mb-2 text-sm">Your Signature</h3>
                <img src={formData.signature} alt="Signature" className="max-h-12" />
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowConfirmation(false)}>
              Edit
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={loading}
              style={primaryButtonStyle}
              className="text-white"
              data-testid="confirm-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Confirm & Submit"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showBacs && bacsConfig && (
        <BacsMandateConfirm
          config={bacsConfig}
          conversionId={conversionId}
          sortCode={formData.sortCode}
          accountNumber={formData.accountNumber}
          accountHolderName={`${formData.firstName} ${formData.lastName}`.trim()}
          email={formData.email}
          ddGuaranteeText={directDebitGuaranteeText}
          onConfirmed={() => {
            setShowBacs(false);
            finalizeConversion(conversionId, campaign?.id);
          }}
          onSkip={() => {
            setShowBacs(false);
            finalizeConversion(conversionId, campaign?.id);
          }}
        />
      )}
    </div>
  );
}
